
import { Options, Vue } from "vue-class-component";
import MultipleSelectPillSearch from "@/app/ui/components/multiple-select-pill-search.vue";
import { RevenueDashboardController } from "@/app/ui/controllers/RevenueDashboardController";

// Icons (import URLs for use in <img src="...">)

// Local option type consumed by MultipleSelectPillSearch
interface FilterOption {
  value: string;
  label: string;
}

// Static Options Interfaces + constants
interface ServiceTypeOption {
  value: "cod" | "non_cod" | "dfod";
  label: string;
}
const SERVICE_TYPE_OPTIONS: ServiceTypeOption[] = [
  { value: "non_cod", label: "Non COD" },
  { value: "cod", label: "COD" },
  { value: "dfod", label: "DFOD" }
];

interface ChannelOption {
  value: "retail" | "marketplace" | "customer_apps" | "cbp";
  label: string;
}
const CHANNEL_OPTIONS: ChannelOption[] = [
  { value: "retail", label: "Retail" },
  { value: "cbp", label: "CBP" }
];

// Controller-side interfaces (kept consistent with controller)
interface ProductTypeOption {
  value: string;
  label: string;
}
interface DestinationCityOption {
  value: string;
  label: string;
}
interface CommodityOption {
  value: string;
  label: string;
}

// BreakdownFilters Interface (controller-aligned)
interface BreakdownFilters {
  serviceTypes: string[];
  channels: string[];
  productTypes: string[];
  destinationCities: string[];
  commodities: string[];
}

// Request payload for breakdown API
import {
  RevenueBreakdownRequest,
  RevenueDashboardState
} from "@/domain/entities/RevenueDashboard";
import { ConvertObjectCamelToSnakeCaseV2 } from "@/app/infrastructures/misc/common-library/ConvertObjectCamelToSnakeCase";
import { PropType } from "vue";

@Options({
  components: { MultipleSelectPillSearch },
  props: {
    // Tambahan untuk request specs params (opsional, biar bisa di-pass dari parent view)
    data: {
      type: Object as () => PropType<RevenueDashboardState>,
      default: () => new RevenueDashboardState()
    }
  },
  emits: ["filter"]
})
export default class RevenueBreakdownFilter extends Vue {
  props: any = this.$props;
  // Fetch initial API-backed options on mount
  mounted(): void {
    RevenueDashboardController.fetchProductTypes("");
    RevenueDashboardController.fetchDestinationCities("", true);
    RevenueDashboardController.fetchCommodities("", true);
  }

  // Access controller state via getters (safe defaults to avoid undefined)
  get activeBreakdownFilters(): BreakdownFilters {
    const f = RevenueDashboardController.activeBreakdownFilters as
      | BreakdownFilters
      | undefined;
    return {
      serviceTypes: f?.serviceTypes ?? [],
      channels: f?.channels ?? [],
      productTypes: f?.productTypes ?? [],
      destinationCities: f?.destinationCities ?? [],
      commodities: f?.commodities ?? []
    };
  }

  // Static options exposed as FilterOption[]
  get serviceTypeOptions(): FilterOption[] {
    return SERVICE_TYPE_OPTIONS.map((o) => ({
      value: o.value,
      label: o.label
    }));
  }

  get channelOptions(): FilterOption[] {
    return CHANNEL_OPTIONS.map((o) => ({
      value: o.value,
      label: o.label
    }));
  }

  // API-based options from controller (mapped to FilterOption[])
  get productTypeOptions(): ProductTypeOption[] {
    return RevenueDashboardController.productTypeOptions;
  }

  get productTypeOptionsMapped(): FilterOption[] {
    return (this.productTypeOptions || []).map((o) => ({
      value: o.value,
      label: o.label
    }));
  }

  get isLoadingProductTypes(): boolean {
    return RevenueDashboardController.isLoadingProductTypes;
  }

  get destinationCityOptions(): DestinationCityOption[] {
    return RevenueDashboardController.destinationCityOptions;
  }

  get destinationCityOptionsMapped(): FilterOption[] {
    return (this.destinationCityOptions || []).map((o) => ({
      value: o.value,
      label: o.value
    }));
  }

  get isLoadingDestinationCities(): boolean {
    return RevenueDashboardController.isLoadingDestinationCities;
  }

  get commodityOptions(): CommodityOption[] {
    return RevenueDashboardController.commodityOptions;
  }

  get commodityOptionsMapped(): FilterOption[] {
    return (this.commodityOptions || []).map((o) => ({
      value: o.value,
      label: o.label
    }));
  }

  get isLoadingCommodities(): boolean {
    return RevenueDashboardController.isLoadingCommodities;
  }

  // Handlers: update controller filters and trigger breakdown
  async handleServiceTypeChange(selected: string[]): Promise<void> {
    const safe = Array.isArray(selected) ? Array.from(selected) : [];
    await RevenueDashboardController.setBreakdownFilter({
      filterType: "serviceTypes",
      values: safe
    });
    this.props.data.codTypes = this.convertFiltersToQueryString(
      this.activeBreakdownFilters.serviceTypes,
      this.getAllFilterOptionValues().serviceTypes
    );
    await this.$nextTick();
    this.triggerBreakdownDataFetch();
  }

  async handleChannelChange(selected: string[]): Promise<void> {
    const safe = Array.isArray(selected) ? Array.from(selected) : [];
    await RevenueDashboardController.setBreakdownFilter({
      filterType: "channels",
      values: safe
    });
    this.props.data.channels = this.convertFiltersToQueryString(
      this.activeBreakdownFilters.channels,
      this.getAllFilterOptionValues().channels
    );
    await this.$nextTick();
    this.triggerBreakdownDataFetch();
  }

  // Helper untuk reset pencarian
  private resetFilterSearch(refName: string): void {
    const filterRef = this.$refs[refName] as any;
    if (filterRef && typeof filterRef.resetSearch === "function") {
      filterRef.resetSearch();
    }
  }

  async handleProductTypeChange(selected: string[]): Promise<void> {
    const safe = Array.isArray(selected) ? Array.from(selected) : [];
    await RevenueDashboardController.setBreakdownFilter({
      filterType: "productTypes",
      values: safe
    });
    this.props.data.productTypes = safe.length > 0 ? safe.join(",") : undefined;

    await this.$nextTick();
    this.triggerBreakdownDataFetch();
    this.resetFilterSearch("productTypeFilter");
  }

  // File: RevenueBreakdownFilter.vue (Perubahan pada handleDestinationCityChange)

async handleDestinationCityChange(selected: string[]): Promise<void> {
  const safe = Array.isArray(selected) ? Array.from(selected) : [];
  await RevenueDashboardController.setBreakdownFilter({
    filterType: "destinationCities",
    values: safe
  });
  if (safe.length === 0) {
    this.props.data.destination3lc = undefined;
  } else {
    this.props.data.destination3lc = safe.join(","); 
  }
  
  await this.$nextTick();
  this.triggerBreakdownDataFetch();
  this.resetFilterSearch("destinationCities");
}

  async handleCommodityChange(selected: string[]): Promise<void> {
    const safe = Array.isArray(selected) ? Array.from(selected) : [];
    await RevenueDashboardController.setBreakdownFilter({
      filterType: "commodities",
      values: safe
    });

    const optionListCommodities = [
      ...this.activeBreakdownFilters.commodities,
      ...this.getAllFilterOptionValues().commodities
    ];
    this.props.data.commodityCodes = this.convertFiltersToQueryString(
      this.activeBreakdownFilters.commodities,
      optionListCommodities
    );
    await this.$nextTick();
    this.triggerBreakdownDataFetch();
     this.resetFilterSearch("commodityFilter");
  }

  handleProductTypeSearch(search: string): void {
    RevenueDashboardController.fetchProductTypes(search ?? "");
  }

  handleDestinationCitySearch(search: string): void {
    RevenueDashboardController.fetchDestinationCities(search ?? "", true);
  }

  handleDestinationCityScroll(): void {
    RevenueDashboardController.loadMoreDestinationCities();
  }

  handleCommoditySearch(search: string): void {
    RevenueDashboardController.fetchCommodities(search ?? "", true);
  }

  handleCommodityScroll(): void {
    RevenueDashboardController.loadMoreCommodities();
  }

  handleProductScroll(): void {
    RevenueDashboardController.loadMoreProductTypes();
  }

  // Drop undefined/null/empty-string keys before sending
  private pruneUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
    const out: Partial<T> = {};
    Object.keys(obj || {}).forEach((k) => {
      const v = (obj as any)[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        (out as any)[k] = v;
      }
    });
    return out;
  }

  // Helper: determine "all selected"
  private isAllValuesSelected(
    selected: string[],
    allOptions: string[]
  ): boolean {
    // Case-insensitive check to decide if all values are selected
    if (!Array.isArray(allOptions) || allOptions.length === 0) return false;

    const selLower = new Set(
      (selected || []).map((v) => String(v).toLowerCase())
    );
    const allLower = new Set(
      (allOptions || []).map((v) => String(v).toLowerCase())
    );

    if (selLower.size < allLower.size) return false;
    for (const v of allLower) {
      if (!selLower.has(v)) return false;
    }
    return selLower.size >= allLower.size;
  }

  private isNoValuesSelected(selected: string[]): boolean {
    return !selected || selected.length === 0;
  }

  private convertFiltersToQueryString(
    selected: string[],
    allOptions: string[]
  ): string | undefined {
    const selectedOriginal = Array.isArray(selected)
      ? Array.from(selected)
      : [];
    const allOriginal = Array.isArray(allOptions) ? Array.from(allOptions) : [];

    if (selectedOriginal.length === 0) return undefined;
    if (allOriginal.length === 0) {
      return selectedOriginal.join(",");
    }
    if (this.isAllValuesSelected(selectedOriginal, allOriginal)) {
      return undefined;
    }

    const allLowerSet = new Set(
      allOriginal.map((v) => String(v).toLowerCase())
    );
    const filteredOriginal = selectedOriginal.filter((v) =>
      allLowerSet.has(String(v).toLowerCase())
    );

    return (filteredOriginal.length > 0
      ? filteredOriginal
      : selectedOriginal
    ).join(",");
  }

  // Collects "all options" for omission logic
  private getAllFilterOptionValues(): {
    serviceTypes: string[];
    channels: string[];
    productTypes: string[];
    destinationCities: string[];
    commodities: string[];
  } {
    const productTypeOptions =
      RevenueDashboardController.productTypeOptions || [];
    const destinationCityOptions =
      RevenueDashboardController.destinationCityOptions || [];
    const commodityOptions = RevenueDashboardController.commodityOptions || [];

    return {
      serviceTypes: SERVICE_TYPE_OPTIONS.map((o) => o.value),
      channels: CHANNEL_OPTIONS.map((o) => o.value),
      productTypes: productTypeOptions.map((o) => o.value),
      destinationCities: destinationCityOptions.map((o) => o.value),
      commodities: commodityOptions.map((o) => o.value)
    };
  }

  // Builds breakdown API request params (partial-only => send; all/none => omit)
  private triggerBreakdownDataFetch(): void {
    this.$emit("filter");
  }
}
